import { CustomModal } from '@components';
import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, Divider, Typography } from '@mui/material';
import { textOrDash } from '@util/textOrDash.ts';
import dayjs from 'dayjs';
import { theme } from '@store';

interface InsuranceBenefits {
  benefit_amount?: string;
  benefit_code?: string;
  benefit_coverage_code?: string;
  benefit_coverage_description?: string;
  benefit_description?: string;
  benefit_notes?: string;
  benefit_period_code?: string;
  benefit_period_description?: string;
  insurance_type_code?: string;
  insurance_type_description?: string;
  plan_date?: string;
}

interface ViewInsuranceBenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payer_name: string;
  payer_type: string;
  payment_type: string;
  benefits: InsuranceBenefits[];
}

export const ViewInsuranceBenefitsModal: FC<
  ViewInsuranceBenefitsModalProps
> = ({ isOpen, onClose, payment_type, payer_type, payer_name, benefits }) => {
  return (
    <CustomModal
      isOpen={isOpen}
      title={t('patient.summary.viewInsuranceModal.title')}
      onClose={onClose}
      subTitle={
        <>
          {payer_name}
          <Box
            sx={{
              height: '4px',
              width: '4px',
              borderRadius: '50%',
              backgroundColor: theme => theme.palette.grey[600],
            }}
          />
          {payer_type}
          <Box
            sx={{
              height: '4px',
              width: '4px',
              borderRadius: '50%',
              backgroundColor: theme => theme.palette.grey[600],
            }}
          />
          {payment_type}
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {benefits.map((item, idx) => {
          return (
            <Box
              key={idx}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Typography variant='body2'>#{idx + 1}</Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  bgcolor: theme => theme.palette.grey[100],
                  padding: '8px 16px',
                }}
              >
                <Typography variant='caption'>
                  {t('patient.summary.viewInsuranceModal.benefitAmount')}
                </Typography>
                <Typography variant='subtitle1'>
                  ${textOrDash(item.benefit_amount)}
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                  {`${item.benefit_code} - ${item.benefit_description}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.insuranceType')}
                </Typography>
                <Typography variant='body2'>
                  {item.insurance_type_code
                    ? `${item.insurance_type_code} - ${item.insurance_type_description}`
                    : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.coverage')}
                </Typography>
                <Typography variant='body2'>
                  {`${item.benefit_coverage_code} - ${item.benefit_coverage_description}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.level')}
                </Typography>
                <Typography variant='body2'>-</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.period')}
                </Typography>
                <Typography variant='body2'>
                  {item.benefit_period_code
                    ? `${item.benefit_period_code} - ${item.benefit_period_description}`
                    : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.entity')}
                </Typography>
                <Typography variant='body2'>-</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.inPlanNetwork')}
                </Typography>
                <Typography variant='body2'>-</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                  {t('patient.summary.viewInsuranceModal.planDate')}
                </Typography>
                <Typography variant='body2'>
                  {item.plan_date
                    ? dayjs(item.plan_date).format('MMM d, YYYY')
                    : '-'}
                </Typography>
              </Box>
              {item.benefit_notes && (
                <Typography variant='body2'>{item.benefit_notes}</Typography>
              )}
              {idx !== benefits.length - 1 && (
                <Divider
                  sx={{ height: 1, bgcolor: theme => theme.palette.grey[300] }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          position: 'sticky',
          bottom: 0,
          paddingY: 3,
          backgroundColor: theme.palette.common.white,
          zIndex: 3,
        }}
      >
        <Button
          size='large'
          onClick={onClose}
          color='secondary'
          variant='contained'
          sx={{ color: theme.palette.common.white }}
        >
          {t('general.close')}
        </Button>
      </Box>
    </CustomModal>
  );
};
